// ⚠ SIMULATED DATA — OPT-IN, SUPER ADMIN ONLY, SCREEN ONLY.
//
// Fakes a week of shifts, bookings and rider legs IN THE BROWSER so the new
// calendar can be reviewed before it is wired to the real reads:
// /calendar?demo=1. Nothing is written anywhere; no real record is touched.
// Assign and Edit shift change only an in-memory list of edits
// (calendar.mock-edits.ts), which a reload throws away.
//
// The same two locks as the live map's simulator (live-map.demo.ts): `?demo=1`
// must be in the URL, and in production only a Super Admin gets it.
//
// The people, times and the 6:40 PM clock are the design's (canvas
// "Bednbody Ops Calendar", 2026-10-22), so the screen can be checked against
// the boards one to one. The output is the REAL read shape
// (`OperationalCalendarReadModels` plus names, clock-ins and skills), so the
// swap to the real hooks changes where a `CalendarSource` comes from and
// nothing that draws it. No client is named anywhere — the real views carry
// no client column either.

import type {
  CalendarDispatchLegKind,
  OperationalCalendarBooking,
  OperationalCalendarDispatch,
  OperationalCalendarStaff,
} from "@bednbody/types";
import { useMemo } from "react";
import { isLiveMapDemo } from "../live-map/live-map.demo";
import type { CalendarSource, LiveState } from "./calendar-board";
import { weekDaysOf } from "./calendar-board.week";
import { dayStartIso, localDateIso } from "./calendar-model";

/** Same switch and locks as the live map's simulator. */
export const isCalendarDemo = isLiveMapDemo;

/** The design's clock: 6:40 PM, so done, happening and coming-up blocks all show. */
export const DEMO_NOW_MINUTES = 18 * 60 + 40;

/** Deterministic fake uuid, so selections survive re-renders. */
function id(n: number): string {
  return `00000000-0000-4000-8000-${String(n).padStart(12, "0")}`;
}

function isoAt(day: string, minutes: number): string {
  return new Date(Date.parse(dayStartIso(day)) + minutes * 60_000).toISOString();
}

const h = (hours: number) => Math.round(hours * 60);

export const DEMO_HUBS = [
  { id: id(900), name: "Cainta" },
  { id: id(901), name: "Pasig" },
] as const;

const [CAINTA, PASIG] = DEMO_HUBS;

type DemoSession = [
  start: number,
  end: number,
  ref: number,
  service: string,
  minutes: number,
  asap?: boolean,
];
type DemoLeg = [kind: CalendarDispatchLegKind, start: number, end: number, ref: number];

interface DemoPerson {
  n: number;
  name: string;
  hub: (typeof DEMO_HUBS)[number];
  role: "therapist" | "rider";
  clockedIn: boolean;
  /** Off on the design's day. */
  offToday?: boolean;
  shift: [start: number, end: number];
  skills?: string[];
  sessions?: DemoSession[];
  legs?: DemoLeg[];
  /** What the live map would add today — one person per state, so every badge shows. */
  live?: LiveState;
}

const PEOPLE: readonly DemoPerson[] = [
  {
    n: 1,
    name: "Ana R.",
    hub: CAINTA,
    role: "therapist",
    clockedIn: true,
    shift: [14, 22],
    skills: ["Swedish", "Ventosa", "Foot Reflex"],
    sessions: [
      [15, 16, 2041, "Swedish", 60],
      [17.5, 19, 2047, "Ventosa", 90, true],
      [20, 21, 2052, "Foot Reflex", 60],
    ],
  },
  {
    n: 2,
    name: "Bea M.",
    hub: CAINTA,
    role: "therapist",
    clockedIn: true,
    shift: [16, 24],
    skills: ["Hot Stone", "Swedish"],
    sessions: [
      [16.5, 18, 2044, "Hot Stone", 90],
      [19.5, 20.5, 2050, "Swedish", 60, true],
      [22, 23, 2058, "Swedish", 60],
    ],
  },
  {
    n: 3,
    name: "Carla D.",
    hub: CAINTA,
    role: "therapist",
    clockedIn: false,
    offToday: true,
    shift: [14, 22],
    skills: ["Swedish", "Ventosa"],
  },
  {
    n: 4,
    name: "Dina S.",
    hub: CAINTA,
    role: "therapist",
    clockedIn: false,
    shift: [18, 24],
    skills: ["Swedish", "Foot Reflex"],
    sessions: [[21, 22.5, 2055, "Ventosa", 90]],
  },
  {
    n: 5,
    name: "Marco T.",
    hub: CAINTA,
    role: "rider",
    clockedIn: true,
    shift: [14, 23],
    legs: [
      ["drop_off", 14.6, 15, 2041],
      ["pickup", 16, 16.4, 2041],
      ["drop_off", 17, 17.5, 2047],
      ["return", 19, 19.5, 2047],
    ],
  },
  {
    n: 6,
    name: "Jun P.",
    hub: CAINTA,
    role: "rider",
    clockedIn: true,
    shift: [16, 24],
    legs: [
      ["drop_off", 16.1, 16.5, 2044],
      ["pickup", 18, 18.4, 2044],
      ["drop_off", 19.1, 19.5, 2050],
      ["pickup", 20.5, 20.9, 2050],
      ["drop_off", 21.5, 22, 2058],
    ],
  },
  {
    n: 7,
    name: "Elle G.",
    hub: PASIG,
    role: "therapist",
    clockedIn: true,
    shift: [14, 22],
    skills: ["Swedish", "Hot Stone"],
    sessions: [
      [14.5, 15.5, 2039, "Swedish", 60, true],
      [17, 18.5, 2048, "Hot Stone", 90],
    ],
  },
  {
    n: 8,
    name: "Faye L.",
    hub: PASIG,
    role: "therapist",
    clockedIn: true,
    shift: [15, 23],
    skills: ["Ventosa", "Swedish"],
    sessions: [
      [17, 18, 2045, "Ventosa", 60],
      [19, 20, 2051, "Swedish", 60],
      [21.5, 22.5, 2057, "Ventosa", 60, true],
    ],
  },
  {
    n: 9,
    name: "Rico V.",
    hub: PASIG,
    role: "rider",
    clockedIn: true,
    shift: [14, 22],
    legs: [
      ["drop_off", 14.1, 14.5, 2039],
      ["return", 15.5, 16, 2039],
      ["drop_off", 16.6, 17, 2048],
      ["pickup", 18.5, 18.9, 2048],
    ],
  },
  // Beyond the design's nine: one person in each live state the map knows.
  {
    n: 10,
    name: "Gina L.",
    hub: CAINTA,
    role: "therapist",
    clockedIn: true,
    shift: [16, 24],
    skills: ["Hot Stone", "Swedish"],
    sessions: [
      [16.5, 18, 2043, "Hot Stone", 90],
      [21.5, 22.5, 2059, "Swedish", 60],
    ],
    live: "waiting_pickup",
  },
  {
    n: 11,
    name: "Hana P.",
    hub: PASIG,
    role: "therapist",
    clockedIn: true,
    shift: [15, 23],
    skills: ["Swedish", "Ventosa"],
    sessions: [
      [15.5, 16.5, 2040, "Swedish", 60],
      [20, 21, 2053, "Ventosa", 60],
    ],
    live: "on_break",
  },
  {
    n: 12,
    name: "Ivy C.",
    hub: CAINTA,
    role: "therapist",
    clockedIn: true,
    shift: [14, 22],
    skills: ["Swedish", "Foot Reflex"],
    sessions: [[18, 19.5, 2046, "Swedish", 90, true]],
    live: "sos",
  },
  {
    n: 13,
    name: "Kiko D.",
    hub: PASIG,
    role: "rider",
    clockedIn: true,
    shift: [15, 23],
    legs: [
      ["drop_off", 15.1, 15.5, 2040],
      ["return", 16.5, 17, 2040],
      ["drop_off", 19.5, 20, 2053],
    ],
    live: "disconnected",
  },
];

/** Bookings nobody has taken yet — today only. */
const UNASSIGNED: readonly DemoSession[] = [
  [19, 20.5, 2061, "Hot Stone", 90, true],
  [21, 22, 2063, "Swedish", 60],
];
const UNASSIGNED_HUB: Record<number, (typeof DEMO_HUBS)[number]> = {
  2061: CAINTA,
  2063: PASIG,
};

const SERVICE_VARIANT: Record<string, string> = {
  Swedish: id(801),
  Ventosa: id(802),
  "Foot Reflex": id(803),
  "Hot Stone": id(804),
};

/**
 * A week of simulated calendar, built around `today`. Today is the design's
 * day exactly; the other six vary it (a rotating day off, a few fewer
 * sessions) so the week view has something to compare.
 */
export function buildDemoCalendarSource(today: string): CalendarSource {
  const days = weekDaysOf(today);
  const todayIndex = days.indexOf(today);
  const nowMin = DEMO_NOW_MINUTES;
  const createdAt = isoAt(days[0]!, 9 * 60);

  const staff: OperationalCalendarStaff[] = [];
  const bookings: OperationalCalendarBooking[] = [];
  const dispatches: OperationalCalendarDispatch[] = [];

  days.forEach((day, dayIndex) => {
    const offset = dayIndex - todayIndex;
    const isToday = offset === 0;
    // Today's clock for today, a closed past or an untouched future otherwise.
    const dayNow = isToday ? nowMin : offset < 0 ? 48 * 60 : -1;
    const refOf = (ref: number) => `BNB-${ref + offset * 100}`;
    const bookingIdOf = (ref: number) => id(10_000 + (dayIndex + 1) * 1_000 + (ref % 1_000));
    const keptRefs = new Set<number>();

    const sessionStatus = (start: number, end: number, assigned: boolean) =>
      !assigned
        ? ("awaiting_therapist" as const)
        : end <= dayNow
          ? ("completed" as const)
          : start <= dayNow
            ? ("in_session" as const)
            : ("pending" as const);

    const addBooking = (
      session: DemoSession,
      hubId: string,
      therapistId: string | null,
      riderId: string | null,
    ) => {
      const [start, end, ref, service, minutes, asap] = session;
      keptRefs.add(ref);
      bookings.push({
        bookingId: bookingIdOf(ref),
        ref: refOf(ref),
        bookingGroupId: null,
        hubId,
        divisionId: null,
        bookingKind: asap ? "asap" : "scheduled",
        serviceVariantId: SERVICE_VARIANT[service] ?? id(800),
        serviceName: service,
        durationMinutes: minutes,
        startsAt: isoAt(day, h(start)),
        endsAt: isoAt(day, h(end)),
        status: sessionStatus(h(start), h(end), therapistId !== null),
        therapistId,
        riderId,
        createdAt,
      });
    };

    PEOPLE.forEach((person, personIndex) => {
      // The design's rotating day off (Cal-17), never landing on today.
      let offDay = (personIndex * 2 + 4) % 7;
      if (offDay === todayIndex) offDay = (offDay + 3) % 7;
      const off = isToday ? person.offToday === true : !person.offToday && dayIndex === offDay;
      staff.push({
        scheduleEntryId: id(3_000 + dayIndex * 20 + person.n),
        scheduleVersionId: id(700),
        hubId: person.hub.id,
        divisionId: null,
        weekStart: days[0]!,
        versionNumber: 1,
        publishedAt: createdAt,
        staffId: id(person.n),
        staffRole: person.role,
        staffActive: true,
        scheduleDate: day,
        kind: off ? "rest_day" : "duty",
        dutyStart: off ? null : isoAt(day, h(person.shift[0])),
        dutyEnd: off ? null : isoAt(day, h(person.shift[1])),
      });
      if (off) return;
      (person.sessions ?? []).forEach((session, index) => {
        // Other days drop about one session in three, so days differ.
        if (!isToday && (index + dayIndex + personIndex) % 3 === 0) return;
        const rider = PEOPLE.find(
          (other) => other.legs?.some((leg) => leg[3] === session[2]) === true,
        );
        addBooking(session, person.hub.id, id(person.n), rider ? id(rider.n) : null);
      });
    });

    if (isToday) {
      for (const session of UNASSIGNED) {
        addBooking(session, UNASSIGNED_HUB[session[2]]!.id, null, null);
      }
    }

    PEOPLE.forEach((person) => {
      (person.legs ?? []).forEach(([kind, start, end, ref], index) => {
        // A leg only exists for a booking that exists that day, and a rider
        // who is off that day has none.
        const riderOff = staff.some(
          (entry) =>
            entry.staffId === id(person.n) &&
            entry.scheduleDate === day &&
            entry.kind === "rest_day",
        );
        if (!keptRefs.has(ref) || riderOff) return;
        dispatches.push({
          dispatchLegId: id(50_000 + dayIndex * 100 + person.n * 10 + index),
          dispatchPlanId: id(60_000 + dayIndex * 100 + (ref % 100)),
          bookingId: bookingIdOf(ref),
          hubId: person.hub.id,
          divisionId: null,
          planRevision: 1,
          planStatus: "planned",
          riderId: id(person.n),
          kind,
          sequence: index + 1,
          fromLocationId: kind === "drop_off" ? "hub" : "client",
          toLocationId: kind === "drop_off" ? "client" : "hub",
          plannedStart: isoAt(day, h(start)),
          plannedEnd: isoAt(day, h(end)),
          estimateDistanceKm: null,
          estimateDurationMin: null,
          estimatedAt: null,
          legStatus: h(end) <= dayNow ? "completed" : h(start) <= dayNow ? "active" : "planned",
          createdAt,
        });
      });
    });
  });

  return {
    models: { staff, bookings, dispatches },
    names: PEOPLE.map((person) => ({
      id: id(person.n),
      name: person.name,
      hubName: person.hub.name,
    })),
    hubs: DEMO_HUBS,
    clockedIn: new Set(PEOPLE.filter((person) => person.clockedIn).map((person) => id(person.n))),
    skills: new Map(
      PEOPLE.filter((person) => person.skills).map((person) => [id(person.n), person.skills!]),
    ),
    live: new Map(
      PEOPLE.flatMap((person) => (person.live ? [[id(person.n), person.live] as const] : [])),
    ),
  };
}

/** The demo's "now": today at the design's 6:40 PM, whatever the real time is. */
export function demoNowMs(today: string): number {
  return Date.parse(dayStartIso(today)) + DEMO_NOW_MINUTES * 60_000;
}

export interface CalendarDemo {
  today: string;
  nowMs: number;
  source: CalendarSource;
}

export function useCalendarDemo(isSuperAdmin: boolean): CalendarDemo | null {
  const enabled = isCalendarDemo(isSuperAdmin);
  return useMemo(() => {
    if (!enabled) return null;
    const today = localDateIso();
    return {
      today,
      nowMs: demoNowMs(today),
      source: buildDemoCalendarSource(today),
    };
  }, [enabled]);
}
